import React, { useState, useEffect } from 'react';
import {useParams, useNavigate} from 'react-router-dom';import {ArrowLeft, Loader2, MessageCircle, Flag, X, UserPlus, Clock, Check} from 'lucide-react';import {API_URL as API} from '@/utils/apiConfig';import {toast} from 'sonner';import axios from 'axios';
import {Button} from '@/components/ui/button';
// Reuse PostCard from CommunityFeed
import {PostCard} from '@/components/CommunityFeed';
import { useTranslation } from 'react-i18next';
const REPORT_REASONS = ['Harassment or bullying', 'Inappropriate content', 'Spam', 'Misinformation', 'Other'];

export default function UserProfilePage({ user }) {
  const {
    t
  } = useTranslation();

  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/feed/posts/user/${userId}`, { withCredentials: true });
        setProfile(res.data.profile);
        setPosts(res.data.posts || []);
      } catch (err) {
        toast.error(t('generated.userprofilepage_failed_to_load_ef5c'));
        navigate('/community');
      }
      setLoading(false);
    };
    load();
  }, [userId, navigate]);

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/feed/posts/${postId}`, { withCredentials: true });
      toast.success(t('generated.userprofilepage_post_deleted_86b4'));
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
    } catch {
      toast.error(t('generated.userprofilepage_failed_to_delete_2f01'));
    }
  };

  const handleDiscuss = async (postId) => {
    try {
      const res = await axios.post(`${API}/feed/posts/${postId}/discuss`, {}, { withCredentials: true });
      toast.success(t('generated.userprofilepage_conversation_created_b185'));
      navigate(`/chat?c=${res.data.conversation_id}`);
    } catch {
      toast.error(t('generated.userprofilepage_failed_b968'));
    }
  };

  const initial = (profile?.name || profile?.username || '?')[0].toUpperCase();
  const isSelf = user?.user_id === userId;
  const isFriend = profile?.is_friend;
  const requestPending = profile?.request_pending;
  const requestSentByMe = profile?.request_sent_by_me;

  const handleSendFriendRequest = async () => {
    setSendingRequest(true);
    try {
      await axios.post(`${API}/community/friends/request`, { user_id: userId }, { withCredentials: true });
      toast.success(t('generated.userprofilepage_friend_request_sent_bf01'));
      setProfile((p) => ({ ...p, request_pending: true, request_sent_by_me: true }));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to send request');
    }
    setSendingRequest(false);
  };

  const handleReportUser = async () => {
    if (!reportReason) { toast.error(t('generated.userprofilepage_please_select_a_0016')); return; }
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/community/report`, {
        target_type: 'user', target_id: userId, reason: reportReason, details: reportDetails.trim() || undefined,
      }, { withCredentials: true });
      toast.success(t('generated.userprofilepage_report_submitted_our_9958'));
      setShowReport(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to submit report');
    }
    setSubmittingReport(false);
  };

  return (
    <div className="page-container relative overflow-x-hidden" style={{ background: 'var(--theme-background)' }} data-testid="user-profile-page">
      {/* Radial glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, var(--theme-accent) 0%, transparent 70%)' }}
      />
      {/* Header */}
      <header
        className="relative z-10 px-5 md:px-8 py-4 flex items-center gap-3"
      >
        <button
          onClick={() => navigate('/community')}
          className="p-2 rounded-full transition-all hover:bg-[var(--theme-hover)]"
          data-testid="profile-back-btn"
        >
          <ArrowLeft className="h-5 w-5" style={{ color: 'var(--theme-text)' }} />
        </button>
        <h1 className="text-lg font-bold truncate" style={{ color: 'var(--theme-text)' }}>
          {loading ? 'Profile' : (profile?.name || 'User')}
        </h1>
      </header>
      <main className="page-content relative z-10 px-5 md:px-8 pb-24 max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : (
          <>
            {/* Profile Card */}
            <div
              className="rounded-2xl p-5 mb-6 text-center"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="profile-card"
            >
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="h-16 w-16 rounded-full object-cover mx-auto mb-3" />
              ) : (
                <div
                  className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3"
                  style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                >
                  {initial}
                </div>
              )}
              <p className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
                {profile?.name || 'Unknown'}
              </p>
              {profile?.username && (
                <p className="text-sm mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                  @{profile.username}
                </p>
              )}
              <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {isFriend ? `${posts.length} ${posts.length === 1 ? 'post' : 'posts'}` : ''}
              </p>

              {/* Action buttons */}
              {!isSelf && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  {!isFriend && !requestPending && (
                    <Button
                      onClick={handleSendFriendRequest}
                      disabled={sendingRequest}
                      size="sm"
                      className="rounded-full px-4"
                      style={{ background: 'var(--theme-accent)', color: '#fff' }}
                      data-testid="add-friend-btn"
                    >
                      {sendingRequest ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}
                      {t('generated.userprofilepage_add_friend_99e5')}
                    </Button>
                  )}
                  {requestPending && requestSentByMe && (
                    <span
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-textSecondary)' }}
                      data-testid="request-sent-badge"
                    >
                      <Clock className="h-3 w-3" />
                      {t('generated.userprofilepage_request_sent_e53f')}
                    </span>
                  )}
                  {requestPending && !requestSentByMe && (
                    <span
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                      data-testid="request-received-badge"
                    >
                      <UserPlus className="h-3 w-3" />
                      {t('generated.userprofilepage_wants_to_be_a2aa')}
                    </span>
                  )}
                  {isFriend && (
                    <span
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ background: 'var(--glass-bg-active)', color: '#22c55e' }}
                      data-testid="friends-badge"
                    >
                      <Check className="h-3 w-3" />
                      {t('generated.userprofilepage_friends_354f')}
                    </span>
                  )}
                  <button
                    onClick={() => setShowReport(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-[var(--theme-hover)]"
                    style={{ color: 'var(--theme-textSecondary)' }}
                    data-testid="report-user-btn"
                  >
                    <Flag className="h-3 w-3" />
                    {t('generated.userprofilepage_report_b280')}
                  </button>
                </div>
              )}
            </div>

            {/* Posts */}
            {!isFriend && !isSelf ? (
              <div className="text-center py-12 rounded-xl" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
                <UserPlus className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
                  {t('generated.userprofilepage_become_friends_to_4841')}
                </p>
                <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                  {t('generated.userprofilepage_send_a_friend_1ee6')}
                </p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
                  {isSelf ? 'You haven\'t posted anything yet' : 'No posts yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((p) => (
                  <PostCard
                    key={p.post_id}
                    post={p}
                    currentUserId={user?.user_id}
                    onDelete={handleDelete}
                    onDiscuss={handleDiscuss}
                    onAuthorClick={() => {}} // Already on profile
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
      {/* Report User Modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="report-user-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReport(false)} />
          <div
            className="relative w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5"
            style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4" style={{ color: '#ef4444' }} />
                <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.userprofilepage_report_user_0ede')}</h3>
              </div>
              <button onClick={() => setShowReport(false)} className="p-1.5 rounded-full hover:bg-[var(--theme-hover)]">
                <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
              </button>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.userprofilepage_why_are_you_5830')} {profile?.name || 'this user'}?
                          </p>
            <div className="space-y-1.5 mb-4">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReportReason(r)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all ${reportReason === r ? 'ring-1' : ''}`}
                  style={{
                    background: reportReason === r ? 'var(--glass-bg-active)' : 'var(--glass-bg)',
                    color: 'var(--theme-text)',
                    ringColor: 'var(--theme-accent)',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
            <textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder={t('generated.userprofilepage_additional_details_optional_10c0')}
              rows={2}
              className="w-full rounded-xl px-3 py-2 text-sm mb-4 resize-none focus:outline-none"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
            />
            <Button
              onClick={handleReportUser}
              disabled={submittingReport || !reportReason}
              className="w-full rounded-xl"
              style={{ background: '#ef4444', color: '#fff' }}
              data-testid="submit-user-report-btn"
            >
              {submittingReport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Flag className="h-4 w-4 mr-2" />}
              {t('generated.userprofilepage_submit_report_c9c4')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
